import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { format, startOfMonth } from "date-fns";
import debounce from "lodash/debounce";
import { connect } from "react-redux";
import { toast } from "react-hot-toast";
import {
  exportBusinessReport,
  fetchReportPurchase,
  fetchReportPurchasesTable,
  fetchReportPayable,
  fetchReportPayablesTable,
  fetchReportReceivable,
  fetchReportReceivablesTable,
  fetchReportSale,
  fetchReportSalesTable,
} from "../../actions/reportAction";
import { formatIDR } from "../../utils/helpers/formatIDR";
import AkasSelect from "../../components/AkasSelect";
import useAkasChartColors from "../../hooks/useAkasChartColors";

const today = format(new Date(), "yyyy-MM-dd");
const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
const ALL_REPORT_ROWS_LIMIT = 100000;

const exportOptions = [
  { format: "xlsx", label: "Excel", icon: "excel" },
  { format: "csv", label: "CSV", icon: "csv" },
  { format: "pdf", label: "PDF", icon: "pdf" },
];

const reportConfig = {
  sales: {
    pretitle: "Keuangan",
    title: "Laporan Penjualan",
    description: "Pantau omzet penjualan, HPP, dan gross profit berdasarkan periode tanggal.",
    chartTitle: "Trend Omzet Penjualan",
    chartName: "Omzet Penjualan",
    tableTitle: "Tabel Penjualan",
    exportType: "sales",
    colorTone: "moneyIn",
    lightColor: "primary",
    amountColor: "primary",
    emptyText: "Belum ada data penjualan pada periode ini.",
    searchPlaceholder: "Cari invoice, customer, metode bayar...",
    summaryLabel: "Total Omzet",
  },
  purchases: {
    pretitle: "Keuangan",
    title: "Laporan Pembelian Material",
    description: "Pantau pengeluaran pembelian bahan/material berdasarkan periode tanggal.",
    chartTitle: "Trend Pembelian Material",
    chartName: "Pembelian Material",
    tableTitle: "Tabel Pembelian Material",
    exportType: "purchases",
    colorTone: "moneyOut",
    lightColor: "danger",
    amountColor: "danger",
    emptyText: "Belum ada pembelian material pada periode ini.",
    searchPlaceholder: "Cari supplier, status, metode bayar...",
    summaryLabel: "Total Pembelian Material",
  },
  receivables: {
    pretitle: "Keuangan",
    title: "Piutang",
    description: "Pantau transaksi penjualan yang belum lunas dan sisa tagihan customer.",
    chartTitle: "Trend Piutang",
    chartName: "Piutang",
    tableTitle: "Table Piutang / Receivables",
    exportType: "receivables",
    colorTone: "warning",
    lightColor: "warning",
    amountColor: "warning",
    emptyText: "Belum ada data piutang pada periode ini.",
    searchPlaceholder: "Cari invoice, customer, metode bayar...",
    summaryLabel: "Total Piutang",
  },
  payables: {
    pretitle: "Keuangan",
    title: "Hutang",
    description: "Pantau pembelian yang belum lunas dan sisa pembayaran ke supplier.",
    chartTitle: "Trend Hutang",
    chartName: "Hutang",
    tableTitle: "Table Hutang / Payables",
    exportType: "payables",
    colorTone: "moneyOutSoft",
    lightColor: "danger",
    amountColor: "danger",
    emptyText: "Belum ada data hutang pada periode ini.",
    searchPlaceholder: "Cari supplier, metode bayar, catatan...",
    summaryLabel: "Total Hutang",
  },
};

const TableSkeletonRows = ({ columns = 8, rows = 6 }) => (
  <>
    {[...Array(rows)].map((_, rowIndex) => (
      <tr key={`report-table-skeleton-${rowIndex}`} style={{ cursor: "loader" }}>
        {[...Array(columns)].map((__, columnIndex) => (
          <td
            key={`report-table-skeleton-${rowIndex}-${columnIndex}`}
            className={columnIndex >= columns - 3 ? "text-end" : ""}>
            <div
              className={`placeholder placeholder-lg ${
                columnIndex === 0 ? "w-50" : columnIndex === 2 ? "w-100" : columnIndex >= columns - 3 ? "w-75" : "w-75"
              }`}></div>
            {columnIndex === 2 && <div className="placeholder placeholder-sm w-50 mt-2"></div>}
          </td>
        ))}
      </tr>
    ))}
  </>
);

const ChartLoadingState = ({ color = "primary" }) => (
  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 360 }}>
    <div className={`spinner-border text-${color}`} role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <tr>
    <td colSpan="8" className="text-center text-secondary py-5">
      {text}
    </td>
  </tr>
);

const ChartEmptyState = ({ text = "Belum ada data pada periode ini." }) => (
  <div
    className="d-flex flex-column align-items-center justify-content-center text-secondary"
    style={{ minHeight: 320 }}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="icon icon-tabler icons-tabler-outline icon-tabler-chart-bar-popular">
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6" />
      <path d="M9 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10" />
      <path d="M15 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14" />
      <path d="M4 20h14" />
    </svg>
    <div className="mt-2">{text}</div>
  </div>
);

const ReportIcon = ({ name, className = "icon icon-1" }) => {
  const icons = {
    search: (
      <>
        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
        <path d="M21 21l-6 -6" />
      </>
    ),
    excel: (
      <>
        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
        <path d="M8 13l4 4" />
        <path d="M12 13l-4 4" />
      </>
    ),
    csv: (
      <>
        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
        <path d="M8 13h8" />
        <path d="M8 17h8" />
      </>
    ),
    pdf: (
      <>
        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
        <path d="M9 17v-4h1.5a1.5 1.5 0 0 1 0 3h-1.5" />
        <path d="M14 13v4" />
        <path d="M14 13h2" />
        <path d="M14 15h1.5" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      {icons[name] || icons.search}
    </svg>
  );
};

const formatPaymentStatus = (status) => {
  if (!status) {
    return "-";
  }

  const normalized = String(status).toLowerCase();
  if (normalized === "paid") {
    return "Lunas";
  }
  if (normalized === "unpaid") {
    return "Belum Lunas";
  }

  return status;
};

const FinancialReportPage = ({
  type = "sales",
  chartData,
  tableData,
  loading,
  tableLoading,
  error,
  fetchReportSale,
  fetchReportPurchase,
  fetchReportReceivable,
  fetchReportPayable,
  fetchReportSalesTable,
  fetchReportPurchasesTable,
  fetchReportReceivablesTable,
  fetchReportPayablesTable,
}) => {
  const config = reportConfig[type] || reportConfig.sales;
  const chartColors = useAkasChartColors();
  const chartColor = chartColors[config.colorTone] || chartColors.neutral;
  const [dateRange, setDateRange] = useState({ startDate: monthStart, endDate: today });
  const [groupBy, setGroupBy] = useState("day");
  const [search, setSearch] = useState("");
  const [filterText, setFilterText] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const tableRows = tableData?.data || [];
  const tableTotal = tableData?.total || tableRows.length;
  const totalValue = chartData?.total || 0;

  const fetchChart = useCallback(() => {
    const chartFetchers = {
      sales: fetchReportSale,
      purchases: fetchReportPurchase,
      receivables: fetchReportReceivable,
      payables: fetchReportPayable,
    };

    chartFetchers[type]?.(groupBy, dateRange.startDate, dateRange.endDate);
  }, [
    dateRange.endDate,
    dateRange.startDate,
    fetchReportPayable,
    fetchReportPurchase,
    fetchReportReceivable,
    fetchReportSale,
    groupBy,
    type,
  ]);

  const fetchTable = useCallback(() => {
    const params = {
      page: 1,
      limit: ALL_REPORT_ROWS_LIMIT,
      search,
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    };

    const tableFetchers = {
      sales: fetchReportSalesTable,
      purchases: fetchReportPurchasesTable,
      receivables: fetchReportReceivablesTable,
      payables: fetchReportPayablesTable,
    };

    tableFetchers[type]?.(params);
  }, [
    dateRange.endDate,
    dateRange.startDate,
    fetchReportPayablesTable,
    fetchReportPurchasesTable,
    fetchReportReceivablesTable,
    fetchReportSalesTable,
    search,
    type,
  ]);

  useEffect(() => {
    fetchChart();
    fetchTable();
  }, [fetchChart, fetchTable]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setFilterText(value);
    debouncedSearch(value);
  };

  const handleApplyFilter = () => {
    fetchChart();
    fetchTable();
  };

  const handleExport = async (formatType) => {
    setIsExporting(true);
    try {
      await exportBusinessReport({
        type: config.exportType,
        format: formatType,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      });
      toast.success(formatType === "pdf" ? "Preview PDF berhasil dibuka." : "Export laporan berhasil.");
    } catch (err) {
      console.error("export error", err);
      toast.error(err?.message || "Gagal export laporan.");
    } finally {
      setIsExporting(false);
    }
  };

  const chartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      colors: [chartColor],
      fill: {
        colors: [chartColor],
        opacity: 1,
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: "48%",
        },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: chartColors.grid,
        strokeDashArray: 4,
      },
      xaxis: {
        categories: chartData?.labels || [],
        labels: {
          style: { colors: "#64748b" },
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`,
          style: { colors: "#64748b" },
        },
      },
      tooltip: {
        y: {
          formatter: (value) => formatIDR(value || 0),
        },
      },
    }),
    [chartColor, chartColors.grid, chartData?.labels],
  );

  const chartSeries = useMemo(
    () => [
      {
        name: config.chartName,
        data: chartData?.data || [],
      },
    ],
    [chartData?.data, config.chartName],
  );

  const renderSalesRows = () =>
    tableRows.map((item, index) => (
      <tr key={item.id}>
        <td>{index + 1}</td>
        <td>{item.sale_date}</td>
        <td>
          <div className="fw-bold">{item.invoice_no || `#${item.id}`}</div>
          <div className="text-secondary small">{formatPaymentStatus(item.payment_status)}</div>
        </td>
        <td>{item.customer_name || "Umum"}</td>
        <td className="text-capitalize">{item.payment_method || "-"}</td>
        <td className={`text-end fw-bold text-${config.amountColor}`}>{formatIDR(item.total || 0)}</td>
        <td className="text-end">{formatIDR(item.total_cogs || 0)}</td>
        <td className={`text-end fw-bold ${(item.gross_profit || 0) >= 0 ? "text-primary" : "text-danger"}`}>
          {formatIDR(item.gross_profit || 0)}
        </td>
      </tr>
    ));

  const renderPurchaseRows = () =>
    tableRows.map((item, index) => (
      <tr key={item.id}>
        <td>{index + 1}</td>
        <td>{item.purchase_date}</td>
        <td>
          <div className="fw-bold">{item.supplier_name || "-"}</div>
          <div className="text-secondary small">{item.note || "Pembelian"}</div>
        </td>
        <td className="text-capitalize">{item.payment_method || "-"}</td>
        <td className={`text-end fw-bold text-${config.amountColor}`}>{formatIDR(item.total || 0)}</td>
        <td className="text-end">{formatIDR(item.pay || 0)}</td>
        <td className={`text-end ${(item.due || 0) > 0 ? "text-danger fw-bold" : ""}`}>{formatIDR(item.due || 0)}</td>
        <td>
          <span className={`badge ${(item.due || 0) > 0 ? "bg-danger-lt" : "bg-success-lt"}`}>
            {formatPaymentStatus(item.payment_status)}
          </span>
        </td>
      </tr>
    ));

  const renderReceivableRows = () =>
    tableRows.map((item, index) => (
      <tr key={item.id}>
        <td>{index + 1}</td>
        <td>{item.sale_date}</td>
        <td>
          <div className="fw-bold">{item.invoice_no || `#${item.id}`}</div>
          <div className="text-secondary small">{formatPaymentStatus(item.payment_status)}</div>
        </td>
        <td>{item.customer_name || "Umum"}</td>
        <td className="text-capitalize">{item.payment_method || "-"}</td>
        <td className="text-end fw-bold">{formatIDR(item.total || 0)}</td>
        <td className="text-end">{formatIDR(item.pay || 0)}</td>
        <td className={`text-end fw-bold text-${config.amountColor}`}>{formatIDR(item.due || 0)}</td>
      </tr>
    ));

  const renderRows = () => {
    if (type === "sales") {
      return renderSalesRows();
    }

    if (type === "receivables") {
      return renderReceivableRows();
    }

    return renderPurchaseRows();
  };

  const renderTableHeader = () => {
    if (type === "sales") {
      return (
        <thead className="table-header">
          <tr>
            <th>No.</th>
            <th>Tanggal</th>
            <th>Invoice</th>
            <th>Customer</th>
            <th>Metode</th>
            <th className="text-end">Total</th>
            <th className="text-end">HPP</th>
            <th className="text-end">Laba Kotor</th>
          </tr>
        </thead>
      );
    }

    if (type === "receivables") {
      return (
        <thead className="table-header">
          <tr>
            <th>No.</th>
            <th>Tanggal</th>
            <th>Invoice</th>
            <th>Customer</th>
            <th>Metode</th>
            <th className="text-end">Total</th>
            <th className="text-end">Bayar</th>
            <th className="text-end">Piutang</th>
          </tr>
        </thead>
      );
    }

    return (
      <thead className="table-header">
        <tr>
          <th>No.</th>
          <th>Tanggal</th>
          <th>Supplier</th>
          <th>Metode</th>
          <th className="text-end">Total</th>
          <th className="text-end">Bayar</th>
          <th className="text-end">{type === "payables" ? "Hutang" : "Sisa"}</th>
          <th>Status</th>
        </tr>
      </thead>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className={`page-pretitle text-${config.lightColor} fw-bold`}>{config.pretitle}</div>
              <h2 className="page-title">{config.title}</h2>
              <div className="text-secondary mt-1">{config.description}</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              {loading ? (
                <div className="placeholder-glow">
                  <div className="placeholder rounded-3" style={{ width: 160, height: 38 }}></div>
                </div>
              ) : (
                <div className={`badge bg-${config.lightColor}-lt fs-3 px-3 py-2`}>{formatIDR(totalValue)}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error?.message || "Terjadi kesalahan saat mengambil data laporan."}
            </div>
          )}

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Tanggal Awal</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.startDate}
                    onChange={(event) => setDateRange({ ...dateRange, startDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tanggal Akhir</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.endDate}
                    onChange={(event) => setDateRange({ ...dateRange, endDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Group Chart</label>
                  <AkasSelect className="form-select" value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                    <option value="day">Harian</option>
                    <option value="week">Mingguan</option>
                    <option value="month">Bulanan</option>
                  </AkasSelect>
                </div>
                <div className="col-md-3">
                  <button className="btn btn-primary w-100" type="button" onClick={handleApplyFilter}>
                    Terapkan Filter
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div>
                    <h3 className="card-title">{config.chartTitle}</h3>
                    {loading ? (
                      <div className="placeholder-glow mt-2">
                        <div className="placeholder rounded-2" style={{ width: 220, height: 12 }}></div>
                      </div>
                    ) : (
                      <p className="text-secondary m-0">
                        {config.summaryLabel}: {formatIDR(totalValue)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingState color={config.lightColor} />
                  ) : !chartData?.data?.some((value) => value > 0) ? (
                    <ChartEmptyState />
                  ) : (
                    <Chart options={chartOptions} series={chartSeries} type="bar" height={360} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card">
                <div className="card-table">
                  <div className="card-header">
                    <div className="row w-full align-items-center g-2">
                      <div className="col-md-5 col-12">
                        <h3 className="card-title mb-0">{config.tableTitle}</h3>
                        {tableLoading ? (
                          <div className="placeholder-glow mt-2">
                            <div
                              className="placeholder rounded-2"
                              style={{ width: 300, maxWidth: "100%", height: 12 }}></div>
                          </div>
                        ) : (
                          <p className="text-secondary m-0">
                            Semua data yang ditemukan pada filter tanggal langsung ditampilkan.
                          </p>
                        )}
                      </div>
                      <div className="col-md-4 col-12">
                        <div className="akas-export-actions justify-content-md-end justify-content-start ms-auto">
                          {exportOptions.map((item) => (
                            <button
                              className="btn btn-1 btn-outline-primary"
                              type="button"
                              key={item.format}
                              disabled={isExporting}
                              onClick={() => handleExport(item.format)}>
                              <ReportIcon name={item.icon} />
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="col-md-3 col-12 my-md-0 my-2">
                        <div className="input-group input-group-flat w-auto">
                          {tableLoading ? (
                            <span className="input-group-text">
                              <div
                                className={`spinner-border spinner-border-sm text-${config.lightColor} text-center`}
                                role="status"></div>
                            </span>
                          ) : (
                            <span className="input-group-text">
                              <ReportIcon name="search" />
                            </span>
                          )}
                          <input
                            id="advanced-table-search"
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            onChange={handleSearchChange}
                            placeholder={config.searchPlaceholder || "Cari..."}
                            value={filterText}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="advanced-table">
                    <div className="table-responsive">
                      <table className="table table-vcenter table-selectable">
                        {renderTableHeader()}
                        <tbody>
                          {tableLoading ? (
                            <TableSkeletonRows columns={8} />
                          ) : tableRows.length ? (
                            renderRows()
                          ) : (
                            <EmptyState text={config.emptyText} />
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="card-footer d-flex align-items-center">
                      {tableLoading ? (
                        <div className="placeholder-glow w-100">
                          <div className="placeholder rounded-2" style={{ width: 210, height: 12 }}></div>
                        </div>
                      ) : (
                        <p className="m-0 text-secondary">
                          Menampilkan <span>{tableRows.length}</span> dari <span>{tableTotal}</span> data
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const type = ownProps.type || "sales";

  return {
    chartData: {
      sales: state?.reports?.sales,
      purchases: state?.reports?.purchases,
      receivables: state?.reports?.receivables,
      payables: state?.reports?.payables,
    }[type],
    tableData: {
      sales: state?.reports?.salesTable,
      purchases: state?.reports?.purchasesTable,
      receivables: state?.reports?.receivablesTable,
      payables: state?.reports?.payablesTable,
    }[type],
    loading: state?.reports?.loading || false,
    tableLoading: state?.reports?.tableLoading || false,
    error: state?.reports?.error,
  };
};

export default connect(mapStateToProps, {
  fetchReportSale,
  fetchReportPurchase,
  fetchReportReceivable,
  fetchReportPayable,
  fetchReportSalesTable,
  fetchReportPurchasesTable,
  fetchReportReceivablesTable,
  fetchReportPayablesTable,
})(FinancialReportPage);
